import React from "react";
import { Divider, Paper, Typography } from "@mui/material";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CumulativeChartTooltipProps {
  /** Horizontal caret position in pixels, relative to the chart container */
  x: number;
  /** Vertical caret position in pixels, relative to the chart container */
  y: number;
  /** Week label shown as the tooltip title, e.g. "W12" */
  weekLabel: string;
  /** Cumulative count line, e.g. "2 of 3 targets mastered" */
  countLabel?: string;
  /** Target name shown in the mastery row — only rendered when provided */
  masteredTarget?: string;
}

// ---------------------------------------------------------------------------
// CumulativeChartTooltip
// ---------------------------------------------------------------------------

/**
 * Floating tooltip for the GoalCumulativeRecordChart.
 *
 * Absolutely positioned relative to the chart container and driven entirely
 * by props — it carries no internal state. The parent is responsible for
 * controlling visibility (mount/unmount) and caret coordinates.
 *
 * @param props
 * @param props.x              - Horizontal caret position (px)
 * @param props.y              - Vertical caret position (px)
 * @param props.weekLabel      - Primary label (week identifier)
 * @param props.countLabel     - Cumulative mastery count string
 * @param props.masteredTarget - Target name for the mastery row
 */
const CumulativeChartTooltip: React.FC<CumulativeChartTooltipProps> = ({
  x,
  y,
  weekLabel,
  countLabel,
  masteredTarget,
}) => {
  return (
    <Paper
      role="tooltip"
      aria-label={`${weekLabel}: ${countLabel ?? ""}`}
      elevation={2}
      sx={{
        position: "absolute",
        left: x,
        top: y,
        transform: "translate(-50%, calc(-100% - 10px))",
        pointerEvents: "none",
        px: 4,
        py: 3,
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 120,
        zIndex: 10,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 500,
          lineHeight: "14px",
          color: "text.primary",
          whiteSpace: "nowrap",
        }}
      >
        {weekLabel}
      </Typography>

      {countLabel && (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 500,
            lineHeight: "14px",
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {countLabel}
        </Typography>
      )}

      {masteredTarget && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              lineHeight: "14px",
              color: "success.light",
            }}
          >
            Mastered: {masteredTarget}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default CumulativeChartTooltip;
